import { useContext, useState, useMemo, useEffect } from 'react'
import {
  Box,
  Tab,
  Tabs,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  Popover,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import TableChart from '@mui/icons-material/TableChart'
import BarChart from '@mui/icons-material/BarChart'
import { MentorClinicsContext } from 'src/mentor/layout'
import ModernizedClinicReportsPage from '../../ClinicOwnerReport/reports/ModernizedClinicReportsPage'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import DateRangeCalendarPicker from './DateRangeCalendarPicker'
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from 'recharts'
import dayjs from 'dayjs'
import dayjsUtc from 'dayjs/plugin/utc'
import dayjsTimezone from 'dayjs/plugin/timezone'
import { REPORT_DATE_TYPES, MOMENT_FORMATS } from 'src/consumer/constants'

dayjs.extend(dayjsUtc)
dayjs.extend(dayjsTimezone)

// Clinic report ranges are computed in the clinic's timezone; default to IST (+05:30), which the
// server also falls back to. Keeps "June 2026" = Jun 1 00:00 – Jun 30 23:59:59 in clinic-local time
// and aligns each daily bucket to a clinic-local calendar day (not a UTC day, which shifted the last
// day out of range and mislabeled the axis).
const CLINIC_TZ = 'Asia/Kolkata'
import {
  fetchClinicBrandReportsForOwner,
  getCorrectReportDateStringForClinicLocationReports,
  getCumulativeClinicBrandReportFromClinicReports,
} from '../../ClinicOwnerReport/reports/utils'
import { toast } from 'react-toastify'

const COLORS = [
  '#7450AA',
  '#FF5F35',
  '#34EBB7',
  '#418571',
  '#97271F',
  '#18411F',
  '#FFA500',
  '#9B59B6',
]

export default function ClinicReportsWithVisualizations({
  // When provided, the page is locked to a single clinic (brand + location) and the brand selector
  // is hidden — used for the inline Stats view on the clinic bookings dashboard, where the main
  // header already shows the clinic. Defaults preserve the standalone /mentor-home/stats behavior.
  lockedClinicBrandId = null,
  clinicLocationIdFilter = null,
} = {}) {
  const clinicsContext = useContext(MentorClinicsContext) || {}
  const { associatedClinicBrands, associatedClinicsData, loadingAssociationsData } = clinicsContext
  const isLockedToClinic = Boolean(lockedClinicBrandId)
  const [activeTab, setActiveTab] = useState(1) // Default to visualizations
  const [selectedClinicBrand, setSelectedClinicBrand] = useState(null)

  // Initialize selected clinic brand when brands are loaded. When locked, prefer the locked brand.
  useEffect(() => {
    if (associatedClinicBrands?.length > 0 && !selectedClinicBrand) {
      const lockedBrand = lockedClinicBrandId
        ? associatedClinicBrands.find((brand) => brand?.id === lockedClinicBrandId)
        : null
      setSelectedClinicBrand(lockedBrand || associatedClinicBrands[0])
    }
  }, [associatedClinicBrands, lockedClinicBrandId])
  const [reportType, setReportType] = useState(REPORT_DATE_TYPES.day)
  const [reports, setReports] = useState(null)
  const [loading, setLoading] = useState(false)
  const [customDateRange, setCustomDateRange] = useState([null, null])
  const [useCustomRange, setUseCustomRange] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null)
  const [monthMenuAnchorEl, setMonthMenuAnchorEl] = useState(null)
  const [tempStartDate, setTempStartDate] = useState(null)
  const [tempEndDate, setTempEndDate] = useState(null)
  const [compareWithPrevious, setCompareWithPrevious] = useState(false)
  const [previousPeriodReports, setPreviousPeriodReports] = useState(null)
  
  // Chart visibility toggles - Total metrics are on by default
  const [chartVisibility, setChartVisibility] = useState({
    consultations: {
      total: true,
      new: false,
      repeat: false,
    },
    patientVisits: {
      total: true,
      new: false,
      repeat: false,
      chartType: 'bar', // 'bar' or 'line'
    },
    therapies: {
      total: true,
      new: false,
    },
  })

  // Calculate previous period date range based on current period
  const getPreviousPeriodRange = (repType, customRange = null) => {
    if (customRange && customRange[0] && customRange[1]) {
      const startDate = dayjs(customRange[0])
      const endDate = dayjs(customRange[1])
      const daysDiff = endDate.diff(startDate, 'day') + 1
      return [
        startDate.subtract(daysDiff, 'day').toDate(),
        startDate.subtract(1, 'day').toDate(),
      ]
    } else if (repType === REPORT_DATE_TYPES.day) {
      const endDate = dayjs().subtract(7, 'days').endOf('day')
      const startDate = endDate.subtract(6, 'days').startOf('day')
      return [startDate.toDate(), endDate.toDate()]
    } else if (repType === REPORT_DATE_TYPES.week) {
      const endDate = dayjs().subtract(7, 'weeks').endOf('week')
      const startDate = endDate.subtract(6, 'weeks').startOf('week')
      return [startDate.toDate(), endDate.toDate()]
    } else if (repType === REPORT_DATE_TYPES.month) {
      const endDate = dayjs().subtract(7, 'months').endOf('month')
      const startDate = endDate.subtract(6, 'months').startOf('month')
      return [startDate.toDate(), endDate.toDate()]
    } else if (repType === REPORT_DATE_TYPES.year) {
      const endDate = dayjs().subtract(7, 'years').endOf('year')
      const startDate = endDate.subtract(6, 'years').startOf('year')
      return [startDate.toDate(), endDate.toDate()]
    }
    return null
  }

  // Fetch reports based on report type or custom date range
  const fetchReportsForVisualization = async (repType, clinicBrandId, customRange = null, fetchPrevious = false) => {
    if (!clinicBrandId) return null

    // Scope all report fetches to a single clinic location when locked (inline dashboard view).
    const reportLocationFilter = clinicLocationIdFilter ? [clinicLocationIdFilter] : []
    setLoading(true)
    try {
      let allReports = null
      let previousReports = null

      // Handle custom date range
      if (customRange && customRange[0] && customRange[1]) {
        // Interpret the picked range as clinic-local calendar dates (default IST), so each daily
        // bucket is a full clinic-local day rather than a UTC day — the UTC bucketing dropped the
        // last calendar day and produced a reversed/off-by-one axis.
        const startYmd = dayjs(customRange[0]).format('YYYY-MM-DD')
        const endYmd = dayjs(customRange[1]).format('YYYY-MM-DD')
        const startDay = dayjs.tz(`${startYmd} 00:00:00`, CLINIC_TZ)
        const endDay = dayjs.tz(`${endYmd} 00:00:00`, CLINIC_TZ)

        // Calculate number of days in range
        const daysDiff = endDay.diff(startDay, 'day') + 1 // +1 to include both start and end dates
        const maxDays = 60 // Limit to 60 days for performance

        const fetchBucket = async (bucketStart, bucketEnd) => {
          const startTime = bucketStart.startOf('day').utc().format(MOMENT_FORMATS.javaInstantFormat)
          const endTime = bucketEnd.endOf('day').utc().format(MOMENT_FORMATS.javaInstantFormat)
          const reports = await fetchClinicBrandReportsForOwner({
            selectedClinicBrandId: clinicBrandId,
            clinicLocationIdsFilter: reportLocationFilter,
            startTime,
            endTime,
            alignToClinicTz: true,
          })
          return { startTime, endTime, ...(reports || {}) }
        }

        if (daysDiff > maxDays) {
          // If range is too large, split into chunks
          const numReports = maxDays
          const step = Math.ceil(daysDiff / numReports)

          allReports = await Promise.all(
            Array.from({ length: numReports }).map(async (_, index) => {
              const bucketStart = startDay.add(index * step, 'day')
              let bucketEnd = bucketStart.add(step - 1, 'day')
              if (bucketEnd.isAfter(endDay)) bucketEnd = endDay
              return fetchBucket(bucketStart, bucketEnd)
            }),
          )
        } else {
          // For smaller ranges, fetch one clinic-local day per bucket
          allReports = await Promise.all(
            Array.from({ length: daysDiff }).map(async (_, index) => {
              const day = startDay.add(index, 'day')
              return fetchBucket(day, day)
            }),
          )
        }
      } else if (repType === REPORT_DATE_TYPES.day) {
        const today = dayjs().endOf('day').utc().format(MOMENT_FORMATS.javaInstantFormat)
        allReports = await Promise.all(
          Array.from({ length: 7 }).map(async (_, index) => {
            const endTime = dayjs.utc(today).subtract(index, 'days').format(MOMENT_FORMATS.javaInstantFormat)
            const startTime = dayjs
              .utc(today)
              .subtract(index + 1, 'days')
              .format(MOMENT_FORMATS.javaInstantFormat)
            const reports = await fetchClinicBrandReportsForOwner({
              selectedClinicBrandId: clinicBrandId,
              clinicLocationIdsFilter: reportLocationFilter,
              startTime,
              endTime,
            })
            return { startTime, endTime, ...(reports || {}) }
          }),
        )
      } else if (repType === REPORT_DATE_TYPES.week) {
        const today = dayjs().endOf('week').utc().format(MOMENT_FORMATS.javaInstantFormat)
        allReports = await Promise.all(
          Array.from({ length: 7 }).map(async (_, index) => {
            const endTime = dayjs.utc(today).subtract(index, 'week').format(MOMENT_FORMATS.javaInstantFormat)
            const startTime = dayjs
              .utc(today)
              .subtract(index + 1, 'week')
              .format(MOMENT_FORMATS.javaInstantFormat)
            const reports = await fetchClinicBrandReportsForOwner({
              selectedClinicBrandId: clinicBrandId,
              clinicLocationIdsFilter: reportLocationFilter,
              startTime,
              endTime,
            })
            return { startTime, endTime, ...(reports || {}) }
          }),
        )
      } else if (repType === REPORT_DATE_TYPES.month) {
        const today = dayjs().endOf('month').utc().format(MOMENT_FORMATS.javaInstantFormat)
        allReports = await Promise.all(
          Array.from({ length: 7 }).map(async (_, index) => {
            const endTime = dayjs.utc(today).subtract(index, 'month').format(MOMENT_FORMATS.javaInstantFormat)
            const startTime = dayjs
              .utc(today)
              .subtract(index + 1, 'month')
              .format(MOMENT_FORMATS.javaInstantFormat)
            const reports = await fetchClinicBrandReportsForOwner({
              selectedClinicBrandId: clinicBrandId,
              clinicLocationIdsFilter: reportLocationFilter,
              startTime,
              endTime,
            })
            return { startTime, endTime, ...(reports || {}) }
          }),
        )
      } else if (repType === REPORT_DATE_TYPES.year) {
        const today = dayjs().endOf('year').utc().format(MOMENT_FORMATS.javaInstantFormat)
        allReports = await Promise.all(
          Array.from({ length: 7 }).map(async (_, index) => {
            const endTime = dayjs.utc(today).subtract(index, 'year').format(MOMENT_FORMATS.javaInstantFormat)
            const startTime = dayjs
              .utc(today)
              .subtract(index + 1, 'year')
              .format(MOMENT_FORMATS.javaInstantFormat)
            const reports = await fetchClinicBrandReportsForOwner({
              selectedClinicBrandId: clinicBrandId,
              clinicLocationIdsFilter: reportLocationFilter,
              startTime,
              endTime,
            })
            return { startTime, endTime, ...(reports || {}) }
          }),
        )
      }

      // Fetch previous period if comparison is enabled
      if (fetchPrevious) {
        const previousRange = getPreviousPeriodRange(repType, customRange)
        if (previousRange && previousRange[0] && previousRange[1]) {
          const prevStartDate = dayjs(previousRange[0]).startOf('day').utc()
          const prevEndDate = dayjs(previousRange[1]).endOf('day').utc()
          
          // Handle different report types for previous period
          if (customRange && customRange[0] && customRange[1]) {
            // Custom range - fetch daily reports
            const prevDaysDiff = prevEndDate.diff(prevStartDate, 'day') + 1
            const maxDays = 60
            
            if (prevDaysDiff > maxDays) {
              const numReports = maxDays
              const step = Math.ceil(prevDaysDiff / numReports)
              previousReports = await Promise.all(
                Array.from({ length: numReports }).map(async (_, index) => {
                  const reportStartDate = prevStartDate.add(index * step, 'day')
                  const reportEndDate = reportStartDate.add(step - 1, 'day')
                  const actualEndDate = reportEndDate.isAfter(prevEndDate) ? prevEndDate : reportEndDate
                  
                  const endTime = actualEndDate.endOf('day').format(MOMENT_FORMATS.javaInstantFormat)
                  const startTime = reportStartDate.startOf('day').format(MOMENT_FORMATS.javaInstantFormat)
                  
                  const reports = await fetchClinicBrandReportsForOwner({
                    selectedClinicBrandId: clinicBrandId,
                clinicLocationIdsFilter: reportLocationFilter,
                    startTime,
                    endTime,
                  })
                  return { startTime, endTime, ...(reports || {}) }
                }),
              )
            } else {
              previousReports = await Promise.all(
                Array.from({ length: prevDaysDiff }).map(async (_, index) => {
                  const reportDate = prevStartDate.add(index, 'day')
                  const endTime = reportDate.endOf('day').format(MOMENT_FORMATS.javaInstantFormat)
                  const startTime = reportDate.startOf('day').format(MOMENT_FORMATS.javaInstantFormat)
                  
                  const reports = await fetchClinicBrandReportsForOwner({
                    selectedClinicBrandId: clinicBrandId,
                clinicLocationIdsFilter: reportLocationFilter,
                    startTime,
                    endTime,
                  })
                  return { startTime, endTime, ...(reports || {}) }
                }),
              )
            }
          } else if (repType === REPORT_DATE_TYPES.day) {
            const prevToday = prevEndDate.format(MOMENT_FORMATS.javaInstantFormat)
            previousReports = await Promise.all(
              Array.from({ length: 7 }).map(async (_, index) => {
                const endTime = dayjs.utc(prevToday).subtract(index, 'days').format(MOMENT_FORMATS.javaInstantFormat)
                const startTime = dayjs.utc(prevToday).subtract(index + 1, 'days').format(MOMENT_FORMATS.javaInstantFormat)
                const reports = await fetchClinicBrandReportsForOwner({
                  selectedClinicBrandId: clinicBrandId,
                clinicLocationIdsFilter: reportLocationFilter,
                  startTime,
                  endTime,
                })
                return { startTime, endTime, ...(reports || {}) }
              }),
            )
          } else if (repType === REPORT_DATE_TYPES.week) {
            const prevToday = prevEndDate.format(MOMENT_FORMATS.javaInstantFormat)
            previousReports = await Promise.all(
              Array.from({ length: 7 }).map(async (_, index) => {
                const endTime = dayjs.utc(prevToday).subtract(index, 'week').format(MOMENT_FORMATS.javaInstantFormat)
                const startTime = dayjs.utc(prevToday).subtract(index + 1, 'week').format(MOMENT_FORMATS.javaInstantFormat)
                const reports = await fetchClinicBrandReportsForOwner({
                  selectedClinicBrandId: clinicBrandId,
                clinicLocationIdsFilter: reportLocationFilter,
                  startTime,
                  endTime,
                })
                return { startTime, endTime, ...(reports || {}) }
              }),
            )
          } else if (repType === REPORT_DATE_TYPES.month) {
            const prevToday = prevEndDate.format(MOMENT_FORMATS.javaInstantFormat)
            previousReports = await Promise.all(
              Array.from({ length: 7 }).map(async (_, index) => {
                const endTime = dayjs.utc(prevToday).subtract(index, 'month').format(MOMENT_FORMATS.javaInstantFormat)
                const startTime = dayjs.utc(prevToday).subtract(index + 1, 'month').format(MOMENT_FORMATS.javaInstantFormat)
                const reports = await fetchClinicBrandReportsForOwner({
                  selectedClinicBrandId: clinicBrandId,
                clinicLocationIdsFilter: reportLocationFilter,
                  startTime,
                  endTime,
                })
                return { startTime, endTime, ...(reports || {}) }
              }),
            )
          } else if (repType === REPORT_DATE_TYPES.year) {
            const prevToday = prevEndDate.format(MOMENT_FORMATS.javaInstantFormat)
            previousReports = await Promise.all(
              Array.from({ length: 7 }).map(async (_, index) => {
                const endTime = dayjs.utc(prevToday).subtract(index, 'year').format(MOMENT_FORMATS.javaInstantFormat)
                const startTime = dayjs.utc(prevToday).subtract(index + 1, 'year').format(MOMENT_FORMATS.javaInstantFormat)
                const reports = await fetchClinicBrandReportsForOwner({
                  selectedClinicBrandId: clinicBrandId,
                clinicLocationIdsFilter: reportLocationFilter,
                  startTime,
                  endTime,
                })
                return { startTime, endTime, ...(reports || {}) }
              }),
            )
          }
        }
      }

      setReports(allReports)
      setPreviousPeriodReports(previousReports)
      return allReports
    } catch (error) {
      console.error('Error fetching reports:', error)
      return null
    } finally {
      setLoading(false)
    }
  }

  // Process cumulative report data for charts
  const processReportsToChartData = (reportsToProcess, isPrevious = false) => {
    if (!reportsToProcess || !Array.isArray(reportsToProcess) || reportsToProcess.length === 0) return []

    const cumulativeReport = getCumulativeClinicBrandReportFromClinicReports(reportsToProcess)
    if (!cumulativeReport || !Array.isArray(cumulativeReport) || cumulativeReport.length === 0) return []

    const rows = cumulativeReport
      .map((report, index) => {
        const originalReport = reportsToProcess[index]
        const reportEndTime = originalReport?.endTime || originalReport?.reportEndTime
        const reportStartTime = originalReport?.startTime || originalReport?.reportStartTime

        // Clinic-tz date of this bucket (default IST). For custom/monthly each bucket is one
        // clinic-local day, so its start is the correct calendar date to label + group by.
        const clinicDate = dayjs.utc(reportStartTime).tz(CLINIC_TZ)
        const date = dayjs.utc(reportEndTime).local() // kept for the rolling day/week/month/year labels
        const labelBasis = useCustomRange ? clinicDate : date
        let dateLabel = ''

        if (useCustomRange) {
          dateLabel = clinicDate.format('MMM DD, YYYY')
        } else if (reportType === REPORT_DATE_TYPES.day) {
          if (!isPrevious && date.isSame(dayjs(), 'day')) dateLabel = 'Today'
          else if (!isPrevious && date.isSame(dayjs().subtract(1, 'day'), 'day')) dateLabel = 'Yesterday'
          else dateLabel = date.format('MMM DD')
        } else if (reportType === REPORT_DATE_TYPES.week) {
          dateLabel = date.format('MMM DD')
        } else if (reportType === REPORT_DATE_TYPES.month) {
          dateLabel = date.format('MMM YYYY')
        } else if (reportType === REPORT_DATE_TYPES.year) {
          dateLabel = date.format('YYYY')
        }

        const suffix = isPrevious ? 'Previous' : 'Current'
        // Format period label for bars (e.g., "Oct'25", "Oct'24")
        const periodLabel = labelBasis.format("MMM'YY")

        return {
          date: dateLabel,
          periodLabel: periodLabel, // For bar labels
          fullDate: labelBasis.format('MMM DD, YYYY'), // More meaningful X-axis
          weekday: labelBasis.format('dddd'), // e.g. "Monday" — for the daily breakdown table
          [`consultations${suffix}`]: report?.metrics?.consultations?.totalBookedCount || 0,
          [`newConsultations${suffix}`]: report?.metrics?.consultations?.newBookedCount || 0,
          [`repeatConsultations${suffix}`]: report?.metrics?.consultations?.repeatBookedCount || 0,
          [`onlineConsultations${suffix}`]: report?.metrics?.consultations?.onlineBookedCount || 0,
          [`inClinicConsultations${suffix}`]: report?.metrics?.consultations?.offlineBookedCount || 0,
          [`patientVisits${suffix}`]: report?.metrics?.patientVisits?.totalBookedCount || 0,
          [`newPatientVisits${suffix}`]: report?.metrics?.patientVisits?.newBookedCount || 0,
          [`repeatPatientVisits${suffix}`]: report?.metrics?.patientVisits?.repeatBookedCount || 0,
          [`therapies${suffix}`]: report?.metrics?.therapies?.totalBookedCount || 0,
          [`newTherapies${suffix}`]: report?.metrics?.therapies?.newBookedCount || 0,
          [`existingTherapies${suffix}`]: report?.metrics?.therapies?.repeatBookedCount || 0,
          [`preScreenings${suffix}`]: report?.metrics?.preScreenings?.totalBookedCount || 0,
          [`postScreenings${suffix}`]: report?.metrics?.postScreenings?.totalBookedCount || 0,
        }
      })

    // Rolling modes fetch newest-first, so reversing yields chronological order. The custom/monthly
    // path already builds buckets oldest-first — reversing it flips the axis (the Jun 29 → Jun 01 bug).
    return useCustomRange ? rows : rows.reverse()
  }

  // Process cumulative report data for charts
  const chartData = useMemo(() => {
    if (!reports || !selectedClinicBrand || !Array.isArray(reports) || reports.length === 0) return []

    const currentData = processReportsToChartData(reports, false)
    
    if (compareWithPrevious && previousPeriodReports && Array.isArray(previousPeriodReports) && previousPeriodReports.length > 0) {
      const previousData = processReportsToChartData(previousPeriodReports, true)
      
      // Merge current and previous data by date
      const maxLength = Math.max(currentData.length, previousData.length)
      return Array.from({ length: maxLength }).map((_, index) => {
        const current = currentData[index] || {}
        const previous = previousData[index] || {}
        
        return {
          date: current.date || previous.date || `Period ${index + 1}`,
          fullDate: current.fullDate || previous.fullDate || current.date || previous.date,
          periodLabelCurrent: current.periodLabel || '',
          periodLabelPrevious: previous.periodLabel || '',
          ...current,
          ...previous,
        }
      })
    }

    // For non-comparison mode, use original structure
    return currentData.map(item => ({
      date: item.date,
      fullDate: item.fullDate,
      weekday: item.weekday,
      consultations: item.consultationsCurrent || 0,
      newConsultations: item.newConsultationsCurrent || 0,
      repeatConsultations: item.repeatConsultationsCurrent || 0,
      onlineConsultations: item.onlineConsultationsCurrent || 0,
      inClinicConsultations: item.inClinicConsultationsCurrent || 0,
      patientVisits: item.patientVisitsCurrent || 0,
      newPatientVisits: item.newPatientVisitsCurrent || 0,
      repeatPatientVisits: item.repeatPatientVisitsCurrent || 0,
      therapies: item.therapiesCurrent || 0,
      newTherapies: item.newTherapiesCurrent || 0,
      existingTherapies: item.existingTherapiesCurrent || 0,
      preScreenings: item.preScreeningsCurrent || 0,
      postScreenings: item.postScreeningsCurrent || 0,
    }))
  }, [reports, previousPeriodReports, reportType, selectedClinicBrand, useCustomRange, compareWithPrevious])

  // X-axis tick density: daily (monthly/custom) views can have ~30 points. Skip labels so they stay
  // readable instead of forcing every tick (which overlapped into an unreadable smear), and angle
  // the labels when dense.
  const denseXAxis = chartData.length > 12
  const xAxisTickInterval = denseXAxis ? Math.ceil(chartData.length / 12) - 1 : 0

  // Calculate totals
  const totals = useMemo(() => {
    if (compareWithPrevious) {
      return chartData.reduce(
        (acc, curr) => ({
          consultations: acc.consultations + (curr.consultationsCurrent || 0),
          consultationsPrevious: acc.consultationsPrevious + (curr.consultationsPrevious || 0),
          newConsultations: acc.newConsultations + (curr.newConsultationsCurrent || 0),
          newConsultationsPrevious: acc.newConsultationsPrevious + (curr.newConsultationsPrevious || 0),
          repeatConsultations: acc.repeatConsultations + (curr.repeatConsultationsCurrent || 0),
          repeatConsultationsPrevious: acc.repeatConsultationsPrevious + (curr.repeatConsultationsPrevious || 0),
          patientVisits: acc.patientVisits + (curr.patientVisitsCurrent || 0),
          patientVisitsPrevious: acc.patientVisitsPrevious + (curr.patientVisitsPrevious || 0),
          therapies: acc.therapies + (curr.therapiesCurrent || 0),
          therapiesPrevious: acc.therapiesPrevious + (curr.therapiesPrevious || 0),
          preScreenings: acc.preScreenings + (curr.preScreeningsCurrent || 0),
          preScreeningsPrevious: acc.preScreeningsPrevious + (curr.preScreeningsPrevious || 0),
          postScreenings: acc.postScreenings + (curr.postScreeningsCurrent || 0),
          postScreeningsPrevious: acc.postScreeningsPrevious + (curr.postScreeningsPrevious || 0),
        }),
        {
          consultations: 0,
          consultationsPrevious: 0,
          newConsultations: 0,
          newConsultationsPrevious: 0,
          repeatConsultations: 0,
          repeatConsultationsPrevious: 0,
          patientVisits: 0,
          patientVisitsPrevious: 0,
          therapies: 0,
          therapiesPrevious: 0,
          preScreenings: 0,
          preScreeningsPrevious: 0,
          postScreenings: 0,
          postScreeningsPrevious: 0,
        },
      )
    }
    
    return chartData.reduce(
      (acc, curr) => ({
        consultations: acc.consultations + (curr.consultations || 0),
        newConsultations: acc.newConsultations + (curr.newConsultations || 0),
        repeatConsultations: acc.repeatConsultations + (curr.repeatConsultations || 0),
        patientVisits: acc.patientVisits + (curr.patientVisits || 0),
        therapies: acc.therapies + (curr.therapies || 0),
        preScreenings: acc.preScreenings + (curr.preScreenings || 0),
        postScreenings: acc.postScreenings + (curr.postScreenings || 0),
      }),
      {
        consultations: 0,
        newConsultations: 0,
        repeatConsultations: 0,
        patientVisits: 0,
        therapies: 0,
        preScreenings: 0,
        postScreenings: 0,
      },
    )
  }, [chartData, compareWithPrevious])

  // Handle comparison toggle
  const handleComparisonToggle = (event) => {
    const enabled = event.target.checked
    setCompareWithPrevious(enabled)
    if (selectedClinicBrand) {
      if (useCustomRange && customDateRange[0] && customDateRange[1]) {
        fetchReportsForVisualization(null, selectedClinicBrand.id, customDateRange, enabled)
      } else {
        fetchReportsForVisualization(reportType, selectedClinicBrand.id, null, enabled)
      }
    }
  }

  // Handle report type change
  const handleReportTypeChange = (newReportType) => {
    setReportType(newReportType)
    setUseCustomRange(false)
    setCustomDateRange([null, null])
    setSelectedMonth(null)
    if (selectedClinicBrand) {
      fetchReportsForVisualization(newReportType, selectedClinicBrand.id, null, compareWithPrevious)
    }
  }

  // Handle month selection from dropdown
  const handleMonthSelect = (monthValue) => {
    if (!monthValue || !selectedClinicBrand) return
    
    setSelectedMonth(monthValue)
    setUseCustomRange(true)
    
    // Parse month value (format: "YYYY-MM")
    const [year, month] = monthValue.split('-')
    const startDate = dayjs(`${year}-${month}-01`).startOf('month')
    const endDate = startDate.endOf('month')
    
    const range = [startDate.toDate(), endDate.toDate()]
    setCustomDateRange(range)
    fetchReportsForVisualization(null, selectedClinicBrand.id, range, compareWithPrevious)
    setMonthMenuAnchorEl(null)
  }

  // Handle custom date range picker open
  const handleCustomRangeOpen = (event) => {
    setDateRangeAnchorEl(event.currentTarget)
    setTempStartDate(customDateRange[0] ? dayjs(customDateRange[0]) : null)
    setTempEndDate(customDateRange[1] ? dayjs(customDateRange[1]) : null)
  }

  // Handle custom date range picker close
  const handleCustomRangeClose = () => {
    setDateRangeAnchorEl(null)
  }

  // Handle monthly menu open
  const handleMonthlyMenuOpen = (event) => {
    setMonthMenuAnchorEl(event.currentTarget)
  }

  // Handle monthly menu close
  const handleMonthlyMenuClose = () => {
    setMonthMenuAnchorEl(null)
  }

  // Handle start date selection in calendar (from DateRangeCalendarPicker)
  const handleStartDateSelect = (date) => {
    setTempStartDate(date)
    // If end date is before start date, clear end date
    if (tempEndDate && tempEndDate.isBefore(date)) {
      setTempEndDate(null)
    }
  }

  // Handle end date selection in calendar (from DateRangeCalendarPicker)
  const handleEndDateSelect = (date) => {
    if (!date || !tempStartDate) return
    
    if (date.isBefore(tempStartDate)) {
      toast.error('End date must be after start date')
      return
    }
    
    setTempEndDate(date)
  }

  // Apply custom date range from calendar
  const handleApplyDateRange = () => {
    if (tempStartDate && tempEndDate && selectedClinicBrand) {
      const range = [tempStartDate.toDate(), tempEndDate.toDate()]
      setCustomDateRange(range)
      setUseCustomRange(true)
      setSelectedMonth(null)
      fetchReportsForVisualization(null, selectedClinicBrand.id, range, compareWithPrevious)
      setDateRangeAnchorEl(null)
    } else {
      toast.error('Please select both start and end dates')
    }
  }

  // Clear custom range
  const handleClearCustomRange = () => {
    setCustomDateRange([null, null])
    setUseCustomRange(false)
    setSelectedMonth(null)
    setTempStartDate(null)
    setTempEndDate(null)
    if (selectedClinicBrand) {
      fetchReportsForVisualization(reportType, selectedClinicBrand.id, null, compareWithPrevious)
    }
    setDateRangeAnchorEl(null)
  }

  // Generate last 12 months options
  const last12Months = useMemo(() => {
    const months = []
    const today = dayjs()
    for (let i = 0; i < 12; i++) {
      const monthDate = today.subtract(i, 'month')
      const value = monthDate.format('YYYY-MM')
      const label = monthDate.format('MMMM YYYY')
      months.push({ value, label })
    }
    return months
  }, [])

  // Handle clinic brand change
  const handleClinicBrandChange = (brand) => {
    setSelectedClinicBrand(brand)
    if (brand) {
      if (useCustomRange && customDateRange[0] && customDateRange[1]) {
        fetchReportsForVisualization(null, brand.id, customDateRange, compareWithPrevious)
      } else {
        fetchReportsForVisualization(reportType, brand.id, null, compareWithPrevious)
      }
    }
  }

  // Fetch reports when component mounts or when selected brand changes
  useEffect(() => {
    if (selectedClinicBrand && activeTab === 1 && !useCustomRange) {
      fetchReportsForVisualization(reportType, selectedClinicBrand.id, null, compareWithPrevious)
    }
  }, [selectedClinicBrand, activeTab, reportType, useCustomRange, compareWithPrevious])

  const renderTableTab = () => (
    <Box sx={{ p: 2 }}>
      <ModernizedClinicReportsPage
        hideMainHeading={isLockedToClinic}
        loadingAssociationsData={loadingAssociationsData}
        associatedClinicBrands={associatedClinicBrands}
        associatedClinics={associatedClinicsData}
        lockedClinicBrandId={lockedClinicBrandId}
        clinicLocationIdFilter={clinicLocationIdFilter}
        hideBrandSelector={isLockedToClinic}
      />
    </Box>
  )

  const renderVisualizationsTab = () => (
    <Box sx={{ p: 3 }}>
      {loadingAssociationsData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : !selectedClinicBrand || associatedClinicBrands?.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No clinic brand available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Clinic Brand Selector — hidden when locked to a single clinic (inline dashboard) */}
          {!isLockedToClinic && (
            <Box sx={{ mb: 2 }}>
              <CustomSelect
                id="clinicBrand"
                label="Clinic Brand"
                value={selectedClinicBrand}
                options={associatedClinicBrands
                  ?.filter((clinicBrand) => clinicBrand?.enabled)
                  .sort((a, b) => (a?.title || a?.brandName)?.localeCompare(b?.title || b?.brandName))}
                isLoading={loadingAssociationsData}
                getOptionValue={(optn) => optn?.id}
                getOptionLabel={(optn) => optn?.title || optn?.brandName}
                onChangeEvent={(e) => {
                  handleClinicBrandChange(e.target.value)
                }}
                selectType={selectTypes.object}
              />
            </Box>
          )}

          {/* Time Scale Selector and Date Range Picker */}
          <Card
            sx={{
              p: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Select Time Period
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={compareWithPrevious}
                    onChange={handleComparisonToggle}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                    Compare with Previous Period
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            {/* Predefined Time Periods */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
              {[
                { id: REPORT_DATE_TYPES.day, label: 'Last 7 Days' },
                { id: REPORT_DATE_TYPES.week, label: 'Last 7 Weeks' },
                { id: REPORT_DATE_TYPES.month, label: 'Last 7 Months' },
                { id: REPORT_DATE_TYPES.year, label: 'Last 7 Years' },
              ].map((type) => (
                <Chip
                  key={type.id}
                  label={type.label}
                  onClick={() => handleReportTypeChange(type.id)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor:
                      !useCustomRange && reportType === type.id
                        ? 'rgba(116, 80, 170, 0.2)'
                        : 'background.paper',
                    color:
                      !useCustomRange && reportType === type.id
                        ? 'primary.main'
                        : 'text.secondary',
                    fontWeight: !useCustomRange && reportType === type.id ? 600 : 400,
                    border: '1px solid',
                    borderColor:
                      !useCustomRange && reportType === type.id
                        ? 'primary.main'
                        : 'divider',
                    '&:hover': {
                      backgroundColor: 'rgba(116, 80, 170, 0.1)',
                      borderColor: 'primary.main',
                    },
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
              
              {/* Custom and Monthly Pills */}
              <Chip
                label="Custom"
                onClick={handleCustomRangeOpen}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: dateRangeAnchorEl
                    ? 'rgba(116, 80, 170, 0.2)'
                    : 'background.paper',
                  color: dateRangeAnchorEl ? 'primary.main' : 'text.secondary',
                  fontWeight: dateRangeAnchorEl ? 600 : 400,
                  border: '1px solid',
                  borderColor: dateRangeAnchorEl ? 'primary.main' : 'divider',
                  '&:hover': {
                    backgroundColor: 'rgba(116, 80, 170, 0.1)',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                }}
              />
              <Chip
                label="Monthly"
                onClick={handleMonthlyMenuOpen}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: monthMenuAnchorEl
                    ? 'rgba(116, 80, 170, 0.2)'
                    : 'background.paper',
                  color: monthMenuAnchorEl ? 'primary.main' : 'text.secondary',
                  fontWeight: monthMenuAnchorEl ? 600 : 400,
                  border: '1px solid',
                  borderColor: monthMenuAnchorEl ? 'primary.main' : 'divider',
                  '&:hover': {
                    backgroundColor: 'rgba(116, 80, 170, 0.1)',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                }}
              />
              
              {useCustomRange && (
                <Chip
                  label={
                    selectedMonth
                      ? dayjs(selectedMonth + '-01').format('MMMM YYYY')
                      : customDateRange[0] && customDateRange[1]
                      ? `${dayjs(customDateRange[0]).format('MMM DD')} - ${dayjs(customDateRange[1]).format('MMM DD, YYYY')}`
                      : 'Custom Range'
                  }
                  onDelete={handleClearCustomRange}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>

            {/* Monthly Menu */}
            <Menu
              anchorEl={monthMenuAnchorEl}
              open={Boolean(monthMenuAnchorEl)}
              onClose={handleMonthlyMenuClose}
              PaperProps={{
                sx: {
                  maxHeight: 300,
                  mt: 1,
                },
              }}
            >
              {last12Months.map((month) => (
                <MenuItem
                  key={month.value}
                  onClick={() => handleMonthSelect(month.value)}
                  selected={selectedMonth === month.value}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {month.label}
                </MenuItem>
              ))}
            </Menu>

            {/* Custom Date Range Picker Popover */}
            <Popover
              open={Boolean(dateRangeAnchorEl)}
              anchorEl={dateRangeAnchorEl}
              onClose={handleCustomRangeClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  p: 2,
                  width: { xs: 'calc(100vw - 32px)', sm: 360 },
                  maxWidth: { xs: 'calc(100vw - 32px)', sm: 360 },
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Select Date Range
                  </Typography>

                  {/* Date Range Calendar Picker */}
                  <DateRangeCalendarPicker
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    onStartDateChange={setTempStartDate}
                    onEndDateChange={setTempEndDate}
                    maxDate={dayjs()}
                  />

                  {/* Action Buttons */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      gap: 1.5, 
                      justifyContent: 'flex-end',
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleClearCustomRange}
                      size="small"
                      sx={{ 
                        textTransform: 'none',
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleApplyDateRange}
                      disabled={!tempStartDate || !tempEndDate}
                      size="small"
                      sx={{
                        textTransform: 'none',
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </LocalizationProvider>
            </Popover>
          </Card>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : chartData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No data available for visualization
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Summary Cards */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #7450AA 0%, #9B59B6 100%)',
                      color: 'white',
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Total Consultations
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {totals.consultations}
                      </Typography>
                      {compareWithPrevious && totals.consultationsPrevious !== undefined && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Previous: {totals.consultationsPrevious}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: totals.consultations > totals.consultationsPrevious ? '#34EBB7' : '#FF5F35',
                              fontWeight: 600,
                            }}
                          >
                            {totals.consultations > totals.consultationsPrevious ? '↑' : '↓'} 
                            {Math.abs(
                              ((totals.consultations - totals.consultationsPrevious) / 
                               (totals.consultationsPrevious || 1)) * 100
                            ).toFixed(1)}%
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #FF5F35 0%, #FF8C69 100%)',
                      color: 'white',
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Patient Visits
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {totals.patientVisits}
                      </Typography>
                      {compareWithPrevious && totals.patientVisitsPrevious !== undefined && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Previous: {totals.patientVisitsPrevious}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: totals.patientVisits > totals.patientVisitsPrevious ? '#34EBB7' : '#FF5F35',
                              fontWeight: 600,
                            }}
                          >
                            {totals.patientVisits > totals.patientVisitsPrevious ? '↑' : '↓'} 
                            {Math.abs(
                              ((totals.patientVisits - totals.patientVisitsPrevious) / 
                               (totals.patientVisitsPrevious || 1)) * 100
                            ).toFixed(1)}%
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #34EBB7 0%, #418571 100%)',
                      color: 'white',
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Therapies
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {totals.therapies}
                      </Typography>
                      {compareWithPrevious && totals.therapiesPrevious !== undefined && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Previous: {totals.therapiesPrevious}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: totals.therapies > totals.therapiesPrevious ? '#34EBB7' : '#FF5F35',
                              fontWeight: 600,
                            }}
                          >
                            {totals.therapies > totals.therapiesPrevious ? '↑' : '↓'} 
                            {Math.abs(
                              ((totals.therapies - totals.therapiesPrevious) / 
                               (totals.therapiesPrevious || 1)) * 100
                            ).toFixed(1)}%
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #97271F 0%, #C0392B 100%)',
                      color: 'white',
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Screenings
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {totals.preScreenings + totals.postScreenings}
                      </Typography>
                      {compareWithPrevious && totals.preScreeningsPrevious !== undefined && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Previous: {(totals.preScreeningsPrevious || 0) + (totals.postScreeningsPrevious || 0)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: (totals.preScreenings + totals.postScreenings) > 
                                     ((totals.preScreeningsPrevious || 0) + (totals.postScreeningsPrevious || 0)) 
                                     ? '#34EBB7' : '#FF5F35',
                              fontWeight: 600,
                            }}
                          >
                            {(totals.preScreenings + totals.postScreenings) > 
                             ((totals.preScreeningsPrevious || 0) + (totals.postScreeningsPrevious || 0)) ? '↑' : '↓'} 
                            {Math.abs(
                              (((totals.preScreenings + totals.postScreenings) - 
                               ((totals.preScreeningsPrevious || 0) + (totals.postScreeningsPrevious || 0))) / 
                               (((totals.preScreeningsPrevious || 0) + (totals.postScreeningsPrevious || 0)) || 1)) * 100
                            ).toFixed(1)}%
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Consultations Chart */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Consultations Overview
                      {compareWithPrevious && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                          (Current vs Previous)
                        </Typography>
                      )}
                    </Typography>
                    {compareWithPrevious && (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={chartVisibility.consultations.total}
                              onChange={(e) =>
                                setChartVisibility((prev) => ({
                                  ...prev,
                                  consultations: { ...prev.consultations, total: e.target.checked },
                                }))
                              }
                            />
                          }
                          label={<Typography variant="caption">Total</Typography>}
                          sx={{ m: 0 }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={chartVisibility.consultations.new}
                              onChange={(e) =>
                                setChartVisibility((prev) => ({
                                  ...prev,
                                  consultations: { ...prev.consultations, new: e.target.checked },
                                }))
                              }
                            />
                          }
                          label={<Typography variant="caption">New</Typography>}
                          sx={{ m: 0 }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={chartVisibility.consultations.repeat}
                              onChange={(e) =>
                                setChartVisibility((prev) => ({
                                  ...prev,
                                  consultations: { ...prev.consultations, repeat: e.target.checked },
                                }))
                              }
                            />
                          }
                          label={<Typography variant="caption">Repeat</Typography>}
                          sx={{ m: 0 }}
                        />
                      </Box>
                    )}
                  </Box>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorConsultations" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#7450AA" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#7450AA" stopOpacity={0.1} />
                        </linearGradient>
                        <linearGradient id="colorConsultationsPrevious" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#9B59B6" stopOpacity={0.6} />
                          <stop offset="95%" stopColor="#9B59B6" stopOpacity={0.1} />
                        </linearGradient>
                        <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF5F35" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#FF5F35" stopOpacity={0.1} />
                        </linearGradient>
                        <linearGradient id="colorNewPrevious" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF8C69" stopOpacity={0.6} />
                          <stop offset="95%" stopColor="#FF8C69" stopOpacity={0.1} />
                        </linearGradient>
                        <linearGradient id="colorRepeat" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#34EBB7" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#34EBB7" stopOpacity={0.1} />
                        </linearGradient>
                        <linearGradient id="colorRepeatPrevious" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#418571" stopOpacity={0.6} />
                          <stop offset="95%" stopColor="#418571" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      {compareWithPrevious ? (
                        <>
                          {chartVisibility.consultations.total && (
                            <>
                              <Area
                                type="monotone"
                                dataKey="consultationsCurrent"
                                stroke="#7450AA"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorConsultations)"
                                name="Current: Total Consultations"
                              />
                              <Area
                                type="monotone"
                                dataKey="consultationsPrevious"
                                stroke="#9B59B6"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                fillOpacity={0.5}
                                fill="url(#colorConsultationsPrevious)"
                                name="Previous: Total Consultations"
                              />
                            </>
                          )}
                          {chartVisibility.consultations.new && (
                            <>
                              <Area
                                type="monotone"
                                dataKey="newConsultationsCurrent"
                                stroke="#FF5F35"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorNew)"
                                name="Current: New Consultations"
                              />
                              <Area
                                type="monotone"
                                dataKey="newConsultationsPrevious"
                                stroke="#FF8C69"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                fillOpacity={0.5}
                                fill="url(#colorNewPrevious)"
                                name="Previous: New Consultations"
                              />
                            </>
                          )}
                          {chartVisibility.consultations.repeat && (
                            <>
                              <Area
                                type="monotone"
                                dataKey="repeatConsultationsCurrent"
                                stroke="#34EBB7"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorRepeat)"
                                name="Current: Repeat Consultations"
                              />
                              <Area
                                type="monotone"
                                dataKey="repeatConsultationsPrevious"
                                stroke="#418571"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                fillOpacity={0.5}
                                fill="url(#colorRepeatPrevious)"
                                name="Previous: Repeat Consultations"
                              />
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <Area
                            type="monotone"
                            dataKey="consultations"
                            stroke="#7450AA"
                            fillOpacity={1}
                            fill="url(#colorConsultations)"
                            name="Total Consultations"
                          />
                          <Area
                            type="monotone"
                            dataKey="newConsultations"
                            stroke="#FF5F35"
                            fillOpacity={1}
                            fill="url(#colorNew)"
                            name="New Consultations"
                          />
                          <Area
                            type="monotone"
                            dataKey="repeatConsultations"
                            stroke="#34EBB7"
                            fillOpacity={1}
                            fill="url(#colorRepeat)"
                            name="Repeat Consultations"
                          />
                        </>
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Daily breakdown — date-wise consultations for a specific month / custom range */}
              {useCustomRange && chartData.length > 0 && (
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                      Daily Breakdown
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Consultations per day for the selected period (clinic time, IST)
                    </Typography>
                    <TableContainer sx={{ maxHeight: 460 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Day</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Total Consultations
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              New
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Repeat
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Online
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              In-Clinic
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Patient Visits
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Therapy / Diet
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Screening
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {chartData.map((row, idx) => {
                            const total = row.consultations ?? row.consultationsCurrent ?? 0
                            const newCount = row.newConsultations ?? row.newConsultationsCurrent ?? 0
                            const repeatCount = row.repeatConsultations ?? row.repeatConsultationsCurrent ?? 0
                            const online = row.onlineConsultations ?? row.onlineConsultationsCurrent ?? 0
                            const inClinic = row.inClinicConsultations ?? row.inClinicConsultationsCurrent ?? 0
                            const visits = row.patientVisits ?? row.patientVisitsCurrent ?? 0
                            const therapies = row.therapies ?? row.therapiesCurrent ?? 0
                            const screenings =
                              (row.preScreenings ?? row.preScreeningsCurrent ?? 0) +
                              (row.postScreenings ?? row.postScreeningsCurrent ?? 0)
                            const isWeekend = row.weekday === 'Sunday' || row.weekday === 'Saturday'
                            return (
                              <TableRow
                                key={`${row.fullDate || row.date}-${idx}`}
                                hover
                                sx={isWeekend ? { backgroundColor: 'rgba(116, 80, 170, 0.04)' } : undefined}
                              >
                                <TableCell>{row.fullDate || row.date}</TableCell>
                                <TableCell sx={{ color: isWeekend ? '#7450AA' : 'text.secondary' }}>
                                  {row.weekday || '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  {total}
                                </TableCell>
                                <TableCell align="right">{newCount}</TableCell>
                                <TableCell align="right">{repeatCount}</TableCell>
                                <TableCell align="right">{online}</TableCell>
                                <TableCell align="right">{inClinic}</TableCell>
                                <TableCell align="right">{visits}</TableCell>
                                <TableCell align="right">{therapies}</TableCell>
                                <TableCell align="right">{screenings}</TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Patient Visits Chart */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Patient Visits
                      {compareWithPrevious && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                          (Current vs Previous)
                        </Typography>
                      )}
                    </Typography>
                    {compareWithPrevious && (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={chartVisibility.patientVisits.total}
                              onChange={(e) =>
                                setChartVisibility((prev) => ({
                                  ...prev,
                                  patientVisits: { ...prev.patientVisits, total: e.target.checked },
                                }))
                              }
                            />
                          }
                          label={<Typography variant="caption">Total</Typography>}
                          sx={{ m: 0 }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={chartVisibility.patientVisits.new}
                              onChange={(e) =>
                                setChartVisibility((prev) => ({
                                  ...prev,
                                  patientVisits: { ...prev.patientVisits, new: e.target.checked },
                                }))
                              }
                            />
                          }
                          label={<Typography variant="caption">New</Typography>}
                          sx={{ m: 0 }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={chartVisibility.patientVisits.repeat}
                              onChange={(e) =>
                                setChartVisibility((prev) => ({
                                  ...prev,
                                  patientVisits: { ...prev.patientVisits, repeat: e.target.checked },
                                }))
                              }
                            />
                          }
                          label={<Typography variant="caption">Repeat</Typography>}
                          sx={{ m: 0 }}
                        />
                        <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2, ml: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={chartVisibility.patientVisits.chartType === 'line'}
                                onChange={(e) =>
                                  setChartVisibility((prev) => ({
                                    ...prev,
                                    patientVisits: { 
                                      ...prev.patientVisits, 
                                      chartType: e.target.checked ? 'line' : 'bar' 
                                    },
                                  }))
                                }
                              />
                            }
                            label={<Typography variant="caption">Worm</Typography>}
                            sx={{ m: 0 }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <ResponsiveContainer width="100%" height={300}>
                    {compareWithPrevious && chartVisibility.patientVisits.chartType === 'line' ? (
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="fullDate"
                          stroke="#666"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={xAxisTickInterval}
                          minTickGap={8}
                        />
                        <YAxis stroke="#666" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                          }}
                        />
                        <Legend />
                        {chartVisibility.patientVisits.total && (
                          <>
                            <Line
                              type="monotone"
                              dataKey="patientVisitsCurrent"
                              stroke="#FF5F35"
                              strokeWidth={3}
                              dot={{ fill: '#FF5F35', r: 5 }}
                              name="Current: Total Visits"
                            />
                            <Line
                              type="monotone"
                              dataKey="patientVisitsPrevious"
                              stroke="#FF8C69"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={{ fill: '#FF8C69', r: 4 }}
                              name="Previous: Total Visits"
                            />
                          </>
                        )}
                        {chartVisibility.patientVisits.new && (
                          <>
                            <Line
                              type="monotone"
                              dataKey="newPatientVisitsCurrent"
                              stroke="#34EBB7"
                              strokeWidth={2}
                              dot={{ fill: '#34EBB7', r: 4 }}
                              name="Current: New Visits"
                            />
                            <Line
                              type="monotone"
                              dataKey="newPatientVisitsPrevious"
                              stroke="#418571"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={{ fill: '#418571', r: 3 }}
                              name="Previous: New Visits"
                            />
                          </>
                        )}
                        {chartVisibility.patientVisits.repeat && (
                          <>
                            <Line
                              type="monotone"
                              dataKey="repeatPatientVisitsCurrent"
                              stroke="#7450AA"
                              strokeWidth={2}
                              dot={{ fill: '#7450AA', r: 4 }}
                              name="Current: Repeat Visits"
                            />
                            <Line
                              type="monotone"
                              dataKey="repeatPatientVisitsPrevious"
                              stroke="#9B59B6"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={{ fill: '#9B59B6', r: 3 }}
                              name="Previous: Repeat Visits"
                            />
                          </>
                        )}
                      </LineChart>
                    ) : (
                      <RechartsBarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey={compareWithPrevious ? "fullDate" : "date"}
                          stroke="#666"
                          angle={denseXAxis || compareWithPrevious ? -45 : 0}
                          textAnchor={denseXAxis || compareWithPrevious ? "end" : "middle"}
                          height={denseXAxis || compareWithPrevious ? 80 : 30}
                          interval={xAxisTickInterval}
                          minTickGap={8}
                        />
                        <YAxis stroke="#666" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                          }}
                        />
                        <Legend />
                        {compareWithPrevious ? (
                          <>
                            {chartVisibility.patientVisits.total && (
                              <>
                                <Bar 
                                  dataKey="patientVisitsCurrent" 
                                  fill="#FF5F35" 
                                  name="Current: Total Visits" 
                                  radius={[8, 8, 0, 0]}
                                >
                                  <LabelList 
                                    dataKey="periodLabelCurrent" 
                                    position="inside" 
                                    angle={-90}
                                    style={{ fill: 'white', fontSize: '11px', fontWeight: 600 }}
                                  />
                                </Bar>
                                <Bar 
                                  dataKey="patientVisitsPrevious" 
                                  fill="#FF8C69" 
                                  name="Previous: Total Visits" 
                                  radius={[8, 8, 0, 0]} 
                                  opacity={0.7}
                                >
                                  <LabelList 
                                    dataKey="periodLabelPrevious" 
                                    position="inside" 
                                    angle={-90}
                                    style={{ fill: 'white', fontSize: '11px', fontWeight: 600 }}
                                  />
                                </Bar>
                              </>
                            )}
                            {chartVisibility.patientVisits.new && (
                              <>
                                <Bar 
                                  dataKey="newPatientVisitsCurrent" 
                                  fill="#34EBB7" 
                                  name="Current: New Visits" 
                                  radius={[8, 8, 0, 0]}
                                >
                                  <LabelList 
                                    dataKey="periodLabelCurrent" 
                                    position="inside" 
                                    angle={-90}
                                    style={{ fill: 'white', fontSize: '11px', fontWeight: 600 }}
                                  />
                                </Bar>
                                <Bar 
                                  dataKey="newPatientVisitsPrevious" 
                                  fill="#418571" 
                                  name="Previous: New Visits" 
                                  radius={[8, 8, 0, 0]} 
                                  opacity={0.7}
                                >
                                  <LabelList 
                                    dataKey="periodLabelPrevious" 
                                    position="inside" 
                                    angle={-90}
                                    style={{ fill: 'white', fontSize: '11px', fontWeight: 600 }}
                                  />
                                </Bar>
                              </>
                            )}
                            {chartVisibility.patientVisits.repeat && (
                              <>
                                <Bar 
                                  dataKey="repeatPatientVisitsCurrent" 
                                  fill="#7450AA" 
                                  name="Current: Repeat Visits" 
                                  radius={[8, 8, 0, 0]}
                                >
                                  <LabelList 
                                    dataKey="periodLabelCurrent" 
                                    position="inside" 
                                    angle={-90}
                                    style={{ fill: 'white', fontSize: '11px', fontWeight: 600 }}
                                  />
                                </Bar>
                                <Bar 
                                  dataKey="repeatPatientVisitsPrevious" 
                                  fill="#9B59B6" 
                                  name="Previous: Repeat Visits" 
                                  radius={[8, 8, 0, 0]} 
                                  opacity={0.7}
                                >
                                  <LabelList 
                                    dataKey="periodLabelPrevious" 
                                    position="inside" 
                                    angle={-90}
                                    style={{ fill: 'white', fontSize: '11px', fontWeight: 600 }}
                                  />
                                </Bar>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Bar dataKey="patientVisits" fill="#FF5F35" name="Total Patient Visits" radius={[8, 8, 0, 0]} />
                            <Bar dataKey="newPatientVisits" fill="#34EBB7" name="New Patient Visits" radius={[8, 8, 0, 0]} />
                            <Bar dataKey="repeatPatientVisits" fill="#7450AA" name="Repeat Patient Visits" radius={[8, 8, 0, 0]} />
                          </>
                        )}
                      </RechartsBarChart>
                    )}
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Therapies and Screenings Comparison */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: 2, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Therapies Trend
                          {compareWithPrevious && (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                              (Current vs Previous)
                            </Typography>
                          )}
                        </Typography>
                        {compareWithPrevious && (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={chartVisibility.therapies.total}
                                  onChange={(e) =>
                                    setChartVisibility((prev) => ({
                                      ...prev,
                                      therapies: { ...prev.therapies, total: e.target.checked },
                                    }))
                                  }
                                />
                              }
                              label={<Typography variant="caption">Total</Typography>}
                              sx={{ m: 0 }}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={chartVisibility.therapies.new}
                                  onChange={(e) =>
                                    setChartVisibility((prev) => ({
                                      ...prev,
                                      therapies: { ...prev.therapies, new: e.target.checked },
                                    }))
                                  }
                                />
                              }
                              label={<Typography variant="caption">New</Typography>}
                              sx={{ m: 0 }}
                            />
                          </Box>
                        )}
                      </Box>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" stroke="#666" />
                          <YAxis stroke="#666" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                            }}
                          />
                          <Legend />
                          {compareWithPrevious ? (
                            <>
                              {chartVisibility.therapies.total && (
                                <>
                                  <Line
                                    type="monotone"
                                    dataKey="therapiesCurrent"
                                    stroke="#34EBB7"
                                    strokeWidth={3}
                                    dot={{ fill: '#34EBB7', r: 5 }}
                                    name="Current: Total Therapies"
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="therapiesPrevious"
                                    stroke="#418571"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={{ fill: '#418571', r: 4 }}
                                    name="Previous: Total Therapies"
                                  />
                                </>
                              )}
                              {chartVisibility.therapies.new && (
                                <>
                                  <Line
                                    type="monotone"
                                    dataKey="newTherapiesCurrent"
                                    stroke="#FF5F35"
                                    strokeWidth={2}
                                    dot={{ fill: '#FF5F35', r: 4 }}
                                    name="Current: New Therapies"
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="newTherapiesPrevious"
                                    stroke="#FF8C69"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={{ fill: '#FF8C69', r: 3 }}
                                    name="Previous: New Therapies"
                                  />
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <Line
                                type="monotone"
                                dataKey="therapies"
                                stroke="#34EBB7"
                                strokeWidth={3}
                                dot={{ fill: '#34EBB7', r: 5 }}
                                name="Total Therapies"
                              />
                              <Line
                                type="monotone"
                                dataKey="newTherapies"
                                stroke="#FF5F35"
                                strokeWidth={2}
                                dot={{ fill: '#FF5F35', r: 4 }}
                                name="New Therapies"
                              />
                            </>
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: 2, height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Screenings Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Pre Screenings', value: totals.preScreenings },
                              { name: 'Post Screenings', value: totals.postScreenings },
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {[
                              { name: 'Pre Screenings', value: totals.preScreenings },
                              { name: 'Post Screenings', value: totals.postScreenings },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 64,
            },
            '& .Mui-selected': {
              color: '#7450AA',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#7450AA',
              height: 3,
            },
          }}
        >
          <Tab icon={<TableChart />} iconPosition="start" label="Table View" sx={{ gap: 1 }} />
          <Tab icon={<BarChart />} iconPosition="start" label="Visualizations" sx={{ gap: 1 }} />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 200px)' }}>
        {activeTab === 0 ? renderTableTab() : renderVisualizationsTab()}
      </Box>
    </Box>
  )
}

